import { inject, injectable } from 'inversify'
import type { MessageSendRepeatYearlyFormScheme, OrdinalDay } from '@/services/domain/messages/types'
import type { SelectFieldConfig, SelectOption } from '@/services/forms/types'
import type {
  FormGroupConfigurationInterface,
  TypedFormBuilderInterface,
} from '@/services/forms/baseForm/typedFormBuilder/types'
import type { Months, Translatable, WeekDaysFull } from '@/services/types'
import { monthsVariant } from '@/services/types'
import { AbstractFormGroupConfiguration } from '@/services/forms/messages/formGroupConfiguration/AbstractFormGroupConfiguration'
import type ComposeScheduledInitFormData from '@/services/domain/compose/ComposeScheduledInitFormData'
import { SERVICE_TYPES } from '@/core/container/types'
import type DateTimeService from '@/services/dateTimeService'

@injectable()
export class YearlyFormGroupConfiguration
  extends AbstractFormGroupConfiguration
  implements FormGroupConfigurationInterface<MessageSendRepeatYearlyFormScheme>
{
  constructor(
    @inject(SERVICE_TYPES.TranslateService) protected readonly translateService: Translatable,
    @inject(SERVICE_TYPES.DateTimeService) protected readonly dateTimeService: DateTimeService,
    @inject(SERVICE_TYPES.ComposeScheduledInitFormData)
    protected readonly composeScheduledInitFormData: ComposeScheduledInitFormData,
  ) {
    super(translateService, dateTimeService)
  }

  private getSelectMonthsOptions(): SelectOption<Months>[] {
    const monthsNames = this.dateTimeService.getMonthsNames('abbreviated')
    return monthsVariant.map((t, index) => ({
      text: monthsNames[index],
      value: t,
    }))
  }

  public configure(builder: TypedFormBuilderInterface<MessageSendRepeatYearlyFormScheme>): void {
    const messageSendRepeatInitFormData = this.composeScheduledInitFormData.getMessageSendRepeatInitFormData()

    builder
      .property((t) => t.count)
      .control({
        fieldType: 'number',
        value: messageSendRepeatInitFormData.yearly.count,
      })
    builder
      .property((t) => t.type)
      .control({
        fieldType: 'radio',
        value: messageSendRepeatInitFormData.yearly.type,
      })
    builder
      .property((t) => t.month)
      .control<SelectFieldConfig<Months>>({
        fieldType: 'select',
        value: messageSendRepeatInitFormData.yearly.month,
        options: () => this.getSelectMonthsOptions(),
      })
    builder
      .property((t) => t.monthDay)
      .control<SelectFieldConfig>({
        fieldType: 'select',
        value: messageSendRepeatInitFormData.yearly.monthDay,
        options: () => this.getSelectMonthDayOptions(),
      })
    builder
      .property((t) => t.firstLastDay)
      .control<SelectFieldConfig<OrdinalDay>>({
        fieldType: 'select',
        value: messageSendRepeatInitFormData.yearly.firstLastDay,
        options: () => this.getSelectOrdinalDaysOptions(),
      })
    builder
      .property((t) => t.weekDay)
      .control<SelectFieldConfig<WeekDaysFull>>({
        fieldType: 'select',
        value: messageSendRepeatInitFormData.yearly.weekDay,
        options: () => this.getSelectWeekDaysOptions(),
      })
    builder
      .property((t) => t.dayOfMonth)
      .control<SelectFieldConfig<Months>>({
        fieldType: 'select',
        value: messageSendRepeatInitFormData.yearly.dayOfMonth,
        options: () => this.getSelectMonthsOptions(),
      })
  }
}
